export interface AuditSignatureInput {
  sessionHash: string;
  timestamp: number;
  source: string;
  auditMode: 'client' | 'server';
}

const roundMetric = (value: number): number => Number(value.toFixed(6));

export function shannonEntropy(values: number[]): number {
  if (values.length === 0) {
    return 0;
  }

  const frequencies = new Map<number, number>();

  for (const value of values) {
    frequencies.set(value, (frequencies.get(value) ?? 0) + 1);
  }

  let entropy = 0;

  for (const occurrences of frequencies.values()) {
    const probability = occurrences / values.length;

    if (probability > 0) {
      entropy -= probability * Math.log2(probability);
    }
  }

  return roundMetric(entropy);
}

export function shannonMax(deckSize: number): number {
  if (!Number.isInteger(deckSize) || deckSize <= 0) {
    return 0;
  }

  return roundMetric(Math.log2(deckSize));
}

export function shannonRatio(values: number[], deckSize: number): number {
  const max = shannonMax(deckSize);

  if (max <= 0) {
    return 0;
  }

  return roundMetric(Math.min(Math.max(shannonEntropy(values) / max, 0), 1));
}

export function previewEntropyHex(bytes: Uint8Array, maxBytes = 16): string {
  const safeMaxBytes = Number.isInteger(maxBytes) && maxBytes > 0 ? maxBytes : 16;
  const preview = bytes.slice(0, safeMaxBytes);
  const hex = Array.from(preview, (value) => value.toString(16).padStart(2, '0')).join('');

  return bytes.byteLength > safeMaxBytes ? `${hex}...` : hex;
}

export function canonicalizeAuditSignatureInput(input: AuditSignatureInput): string {
  return JSON.stringify({
    auditMode: input.auditMode,
    sessionHash: input.sessionHash,
    source: input.source,
    timestamp: input.timestamp,
  });
}
